/**
 * Maps a notification event type + data payload to an in-app URL.
 *
 * Pure function so it can be called from three places that all need the same
 * routing table: the in-app notification bell, the foreground push toast, and
 * the cold-launch push tap handler. Changing the routing rules in one place
 * keeps every surface consistent.
 *
 * Returns `null` only when the orgSlug is missing (e.g. a push payload that
 * predates the orgSlug embedding); in that case callers should fall back to
 * the landing screen.
 */

export type NotificationData = Record<string, unknown>

export function getNotificationDeepLink(
  type: string,
  data: NotificationData,
  orgSlug: string
): string | null {
  if (!orgSlug) return null
  const d = data as Record<string, string | undefined>
  const base = `/in/${orgSlug}`

  switch (type) {
    case 'feed_comment':
    case 'feed_comment_reply':
    case 'feed_reaction':
    case 'feed_mention':
    case 'admin_post':
    case 'chapter_post':
      return d.postId ? `${base}?tab=home#post-${d.postId}` : base

    case 'dm_received':
      return d.conversationId ? `${base}/messages/${d.conversationId}` : `${base}/messages`

    case 'connection_request':
    case 'connection_accepted':
      return d.targetId ? `${base}/profile/${d.targetId}` : `${base}?tab=directory`

    case 'chapter_invite':
      return d.chapterId ? `${base}/chapters/${d.chapterId}` : `${base}/chapters`

    case 'event_reminder':
    case 'event_rsvp_new':
    case 'event_approved':
    case 'event_rejected':
      return d.eventId ? `${base}/events/${d.eventId}` : `${base}/events`

    case 'travel_match':
      return d.travelerId ? `${base}/profile/${d.travelerId}` : `${base}?tab=directory`

    // Legacy type (pre-jobs pivot): stored notifications keep working because
    // the /opportunities/:id shim resolves the migrated job and redirects.
    case 'opportunity_posted':
      return d.opportunityId ? `${base}/opportunities/${d.opportunityId}` : `${base}/jobs`

    // Jobs detail route accepts slug or UUID; fall back to the board.
    // Multi-post performance nudges land on the board (My Posts lives there).
    case 'job_posted':
    case 'job_performance':
      if (type === 'job_performance' && Number(d.jobCount ?? 1) > 1) {
        return `${base}/jobs`
      }
      return d.jobSlug
        ? `${base}/jobs/${d.jobSlug}`
        : d.jobId
          ? `${base}/jobs/${d.jobId}`
          : `${base}/jobs`

    // Admin pending-action types: deep-link straight into the queue surface
    // an admin can act from. Push tapped on phone → admin lands in the
    // mobile-optimized action queue (v1.64.0) ready to approve / reject.
    case 'member_pending':
      return `${base}/admin/members?tab=action-queue`

    case 'event_pending':
      return d.chapterId && d.eventId
        ? `${base}/admin/chapters/${d.chapterId}/events/${d.eventId}`
        : `${base}/admin/events?status=pending_approval`

    // Mentorship: outreach + intro land the recipient in the Inbox thread;
    // the admin review queue lives on the admin Mentoring screen.
    case 'mentorship_outreach':
    case 'mentorship_matched':
      // Day-7 outcome prompt / limbo closing warning: land on the mentorship
      // view where the resolve dialog lives, not the raw thread.
      if (d.followup || d.closing) return `${base}?tab=community&sub=mentorship`
      return d.conversationId ? `${base}/messages/${d.conversationId}` : `${base}/messages`

    case 'mentorship_review_pending':
      return `${base}/admin/mentorship`

    // No-match closure lands the seeker back on their mentorship view (the
    // "My requests" surface), where the ask now reads "the team will follow up".
    case 'mentorship_no_match':
      return `${base}?tab=community&sub=mentorship`

    default:
      return base
  }
}
